import {
    Combobox as HeadlessUICombobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOptions,
} from "@headlessui/react";
import classnames from "classnames";
import * as React from "react";

import assets from "../../common/assets";
import styles from "./Combobox.module.scss";
import type { ComboboxSize, ComboboxTheme } from "./Combobox.types";
import { getSize, getTheme } from "./Combobox.utils";

type ComboboxProps<TValue> = Omit<
    React.ComponentPropsWithoutRef<typeof HeadlessUICombobox<TValue, false>>,
    "onChange" | "multiple"
> & {
    query: string;
    itemCount: number;
    displayName: (value: TValue) => string;
    onQueryChange: (value: string) => void;
    onValueChange: (value: TValue) => void;
    theme?: ComboboxTheme;
    size?: ComboboxSize;
    placeholder: string;
    children?: React.ReactNode;
};

const Combobox = <TValue,>({
    query,
    itemCount,
    onQueryChange,
    onValueChange,
    displayName,
    theme = "green",
    size = 40,
    placeholder,
    children,
    ...rest
}: ComboboxProps<TValue>) => {
    const comboboxTheme = getTheme(theme);
    const comboboxSize = getSize(size);

    return (
        <HeadlessUICombobox
            {...rest}
            onChange={(value) => {
                if (value != null) {
                    onValueChange(value);
                }
            }}>
            <div className={classnames(styles.Combobox, comboboxTheme)}>
                <div className={classnames(styles.ComboboxInputContainer, comboboxSize)}>
                    <ComboboxInput<TValue | null>
                        className={styles.ComboboxInput}
                        placeholder={placeholder}
                        displayValue={(value) => (value == null ? "" : displayName(value))}
                        onChange={(event) => onQueryChange(event.target.value)}
                    />
                    <ComboboxButton
                        className={styles.ComboboxInputIcon}
                        aria-label="Öppna listan">
                        <div className={styles.ComboboxIconWrapper}>{assets.angle}</div>
                    </ComboboxButton>
                </div>
                <ComboboxOptions className={styles.OptionsListContainer} as="div">
                    <div className={styles.OptionsList}>
                        {query && itemCount === 0 && (
                            <li className={classnames(styles.Option, styles.OptionNotFound)}>
                                Inget hittat.
                            </li>
                        )}
                        {children}
                    </div>
                </ComboboxOptions>
            </div>
        </HeadlessUICombobox>
    );
};

export default Combobox;
